import { PlusCircleFilled } from '@ant-design/icons'
import { Button, Flex, Form, type InputProps, Tabs } from 'antd'
import MultiLinesInput from './MultiLinesInput'
import TextInput from './TextInput'
import type { RequiredProps, WithSourceFormProps } from './withForm'

/*
 * Component
 */
export type AddressFormProps = Omit<RequiredProps, 'translatedStrings'> &
  WithSourceFormProps<{
    translatedStrings: {
      addName: string
      addNameDescription: string
      business: string
      cancel: string
      city: string
      cityPlaceholder: string
      country: string
      countryPlaceholder: string
      nameForSources: string
      namePlaceholderForSources: string
      nameForBusinessSources: string
      namePlaceholderForBusinessSources: string
      nCharsField: (n: number) => string
      private: string
      requiredField: string
      street: string
      streetPlaceholder: string
      submit: string
      zip: string
      zipPlaceholder: string
      countryCode?: string
    }
    loading?: boolean
    onCancel?: () => unknown
    disableTypeSwitch?: boolean
  }>

export const SourceForm = ({
  initialValues,
  values: {
    business = initialValues.business,
    lines,
    streetAddress,
    city,
    zip,
    countryCode,
  },
  dirty,
  resetForm,
  handleChange,
  handleBlur,
  handleSubmit,
  errors,
  isValid,
  isSubmitting,
  touched,
  setFieldValue,
  loading,
  onCancel,
  translatedStrings,
  formConfig,
  disableTypeSwitch = false,
}: AddressFormProps) => {
  const canSubmit = !dirty || !isValid
  const canAddName = lines.length < formConfig.maxNames
  const defaultFieldProps: InputProps = {
    onBlur: handleBlur,
    onChange: handleChange,
    disabled: isSubmitting,
    name: '',
    placeholder: '',
    value: '',
  }

  return (
    <Form
      layout="vertical"
      onFinish={handleSubmit}
      style={{
        width: '100%',
        padding: '0',
        maxWidth: 624,
        margin: '0 auto',
      }}
    >
      {/* Business toggle */}
      <Tabs
        activeKey={business ? 'business' : 'private'}
        onChange={key => setFieldValue('business', key === 'business')}
        items={[
          {
            key: 'private',
            label: translatedStrings.private,
            disabled: disableTypeSwitch && !!business,
          },
          {
            key: 'business',
            label: translatedStrings.business,
            disabled: disableTypeSwitch && !business,
          },
        ]}
      />

      {/* Name field */}
      {business ? (
        <TextInput
          error={errors.lines?.[0]}
          touched={touched.lines?.[0] && initialValues.lines[0] !== lines[0]}
          label={
            business
              ? translatedStrings.nameForBusinessSources
              : translatedStrings.nameForSources
          }
          inputProps={{
            ...defaultFieldProps,
            autoFocus: true,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              setFieldValue('lines.0', e.target.value),
            name: 'lines.0',
            value: lines[0] || '',
            placeholder: business
              ? translatedStrings.namePlaceholderForBusinessSources
              : translatedStrings.namePlaceholderForSources,
          }}
        />
      ) : (
        // TODO: review validation logic with multilines, seems broken since
        // "touched" only covers one value, but inside multilines input we assume
        // there is one separate field per line!
        <MultiLinesInput
          inputProps={{ ...defaultFieldProps }}
          errors={Array.isArray(errors.lines) ? errors.lines : [errors.lines]}
          touched={
            Array.isArray(touched.lines) ? touched.lines : [touched.lines]
          }
          label={translatedStrings.nameForSources}
          setFieldValue={setFieldValue}
          values={lines}
          initialValues={initialValues.lines}
          onBlur={handleBlur}
          placeholder={translatedStrings.namePlaceholderForSources}
          disabled={isSubmitting}
        />
      )}

      {/* "New name" function, only displayed for private plans; disabled once
          the DPAG name limit is reached */}
      {business === false && (
        <Flex
          align="center"
          style={{
            marginBottom: 20,
            cursor: canAddName ? 'pointer' : 'not-allowed',
            opacity: canAddName ? 1 : 0.5,
          }}
          onClick={
            canAddName
              ? () => setFieldValue('lines', [...lines, ''])
              : undefined
          }
        >
          <PlusCircleFilled
            style={{
              color: '#00bbff',
              fontSize: '24px',
              marginRight: '4px',
            }}
          />
          <div style={{ marginLeft: 12 }}>
            <p style={{ fontSize: 12, margin: 0 }}>
              {translatedStrings.addName}
            </p>
            <span style={{ fontSize: 12, color: '#9aabc1' }}>
              {translatedStrings.addNameDescription}
            </span>
          </div>
        </Flex>
      )}

      {/* Street address field */}
      <TextInput
        error={errors.streetAddress}
        touched={
          !!touched.streetAddress &&
          initialValues.streetAddress !== streetAddress
        }
        label={translatedStrings.street}
        inputProps={{
          ...defaultFieldProps,
          name: 'streetAddress',
          value: streetAddress,
          placeholder: translatedStrings.streetPlaceholder,
        }}
      />

      <Flex gap={16}>
        <TextInput
          style={{ marginRight: '16px', flexGrow: 0 }}
          error={errors.zip}
          touched={!!touched.zip && initialValues.zip !== zip}
          label={translatedStrings.zip}
          inputProps={{
            ...defaultFieldProps,
            name: 'zip',
            value: zip,
            placeholder: translatedStrings.zipPlaceholder,
          }}
        />
        <TextInput
          style={{ flexGrow: 1 }}
          error={errors.city}
          touched={!!touched.city && initialValues.city !== city}
          label={translatedStrings.city}
          inputProps={{
            ...defaultFieldProps,
            name: 'city',
            value: city,
            placeholder: translatedStrings.cityPlaceholder,
          }}
        />
      </Flex>

      <TextInput
        error={errors.countryCode}
        touched={
          !!touched.countryCode && initialValues.countryCode !== countryCode
        }
        label={translatedStrings.country}
        inputProps={{
          ...defaultFieldProps,
          disabled: true,
          name: 'countryCode',
          value: 'Deutschland',
          placeholder: translatedStrings.countryPlaceholder,
        }}
      />

      <Flex gap={16} style={{ width: '100%', marginTop: 10 }}>
        <Button
          type="default"
          style={{ flex: 1 }}
          onClick={() => {
            resetForm()
            if (onCancel) {
              onCancel()
            }
          }}
        >
          {translatedStrings.cancel}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          style={{ flex: 1 }}
          disabled={canSubmit}
          loading={loading || isSubmitting}
        >
          {translatedStrings.submit}
        </Button>
      </Flex>
    </Form>
  )
}
